// src/features/hotels/components/NotifierConfigSection.tsx

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Alert,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import { useTranslate, useNotify, useDataProvider } from 'react-admin';
import { MyFormSection } from '@/components/forms/MyFormLayout';
import type {
  NotifierConfiguration,
  ChannelConfig,
  AlertMapping,
  NotifierTemplate,
} from '@/types/models';
import { ALERT_TYPES } from '@/types/models';

interface NotifierConfigSectionProps {
  hotelId: string;
}

// Fixed channel types - always shown
const FIXED_CHANNELS = ['EMAIL', 'SMS', 'WHATSAPP'] as const;

/**
 * Get channel icon component
 */
const getChannelIconComponent = (channelType: string) => {
  switch (channelType) {
    case 'EMAIL':
      return <EmailIcon />;
    case 'SMS':
      return <SmsIcon />;
    case 'WHATSAPP':
      return <WhatsAppIcon />;
    default:
      return <NotificationsIcon />;
  }
};

/**
 * Validate recipient format based on channel type
 */
const validateRecipient = (recipient: string, channelType: string): boolean => {
  if (!recipient || !recipient.trim()) return false;

  if (channelType === 'EMAIL') {
    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(recipient);
  } else {
    // Phone number validation for SMS and WHATSAPP
    // Accept formats: +34123456789, 34123456789, 123456789
    const phoneRegex = /^\+?[0-9]{9,15}$/;
    return phoneRegex.test(recipient.replace(/\s/g, ''));
  }
};

/**
 * Get placeholder text based on channel type
 */
const getRecipientPlaceholder = (channelType: string, translate: (key: string) => string): string => {
  if (channelType === 'EMAIL') {
    return translate('resources.hotels.notifier.placeholders.email');
  } else {
    return translate('resources.hotels.notifier.placeholders.phone');
  }
};

/**
 * Notifier Configuration Section
 * Fixed sections for EMAIL, SMS, and WHATSAPP channels
 */
export const NotifierConfigSection: React.FC<NotifierConfigSectionProps> = ({ hotelId }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();

  // State
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<NotifierConfiguration | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [templates, setTemplates] = useState<Record<string, NotifierTemplate[]>>({});
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  // Load configuration immediately when component mounts
  useEffect(() => {
    loadConfiguration();
  }, [hotelId]);

  // Load templates when credentials are configured
  useEffect(() => {
    if (config?.apiKey && config?.accountName) {
      loadTemplatesForAllChannels();
    }
  }, [config?.apiKey, config?.accountName]);

  // Warn user about unsaved changes before leaving
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasChanges) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasChanges]);

  /**
   * Initialize default channels (EMAIL, SMS, WHATSAPP)
   */
  const initializeDefaultChannels = (): ChannelConfig[] => {
    return FIXED_CHANNELS.map(type => ({
      type,
      enabled: false,
      recipients: [],
      alertMappings: [],
    }));
  };

  /**
   * Load notifier configuration from API
   */
  const loadConfiguration = async () => {
    setLoading(true);
    try {
      const { data } = await dataProvider.getNotifierConfig(hotelId);
      
      if (data) {
        // Ensure all fixed channels exist
        const existingChannels = data.channels || [];
        const normalizedChannels = FIXED_CHANNELS.map(channelType => {
          const existing = existingChannels.find(c => c.type === channelType);
          return existing || {
            type: channelType,
            enabled: false,
            recipients: [],
            alertMappings: [],
          };
        });

        setConfig({
          ...data,
          channels: normalizedChannels,
        });
      } else {
        // Initialize with default channels
        setConfig({
          apiKey: '',
          accountName: '',
          channels: initializeDefaultChannels(),
        });
      }
    } catch (error) {
      console.error('Error loading notifier config:', error);
      // Initialize with default channels on error
      setConfig({
        apiKey: '',
        accountName: '',
        channels: initializeDefaultChannels(),
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Load templates for all fixed channel types
   */
  const loadTemplatesForAllChannels = async () => {
    setLoadingTemplates(true);
    
    try {
      const templatesMap: Record<string, NotifierTemplate[]> = {};
      
      // Load templates for each fixed channel type
      for (const channelType of FIXED_CHANNELS) {
        try {
          const { data } = await dataProvider.getNotifierTemplates(hotelId, channelType);
          templatesMap[channelType] = data || [];
          
          console.log(`Loaded ${data?.length || 0} templates for ${channelType}`);
        } catch (error) {
          console.error(`Error loading templates for ${channelType}:`, error);
          templatesMap[channelType] = [];
        }
      }
      
      setTemplates(templatesMap);
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  /**
   * Save configuration
   */
  const handleSave = async () => {
    if (!config) return;

    // Validate
    if (!config.apiKey || !config.accountName) {
      notify('resources.hotels.notifier.validation.credentials_required', { type: 'error' });
      return;
    }

    setSaving(true);
    try {
      await dataProvider.updateNotifierConfig(hotelId, config);
      notify('resources.hotels.notifier.messages.save_success', { type: 'success' });
      setHasChanges(false);
    } catch (error: any) {
      const errorMessage = error?.message || translate('resources.hotels.notifier.messages.save_error');
      notify(errorMessage, { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  /**
   * Update API credentials
   */
  const updateCredentials = (field: 'apiKey' | 'accountName', value: string) => {
    if (!config) return;
    
    setConfig({
      ...config,
      [field]: value,
    });
    setHasChanges(true);
  };

  /**
   * Update channel configuration by type
   */
  const updateChannel = (channelType: string, updates: Partial<ChannelConfig>) => {
    if (!config) return;

    const updatedChannels = config.channels.map(channel =>
      channel.type === channelType
        ? { ...channel, ...updates }
        : channel
    );

    setConfig({
      ...config,
      channels: updatedChannels,
    });
    setHasChanges(true);
  };

  /**
   * Add recipient to channel
   */
  const addRecipient = (channelType: string, recipient: string) => {
    if (!config || !recipient.trim()) return;

    // Validate format
    if (!validateRecipient(recipient, channelType)) {
      const errorKey = channelType === 'EMAIL' 
        ? 'resources.hotels.notifier.validation.invalid_email'
        : 'resources.hotels.notifier.validation.invalid_phone';
      notify(errorKey, { type: 'error' });
      return;
    }

    const channel = config.channels.find(c => c.type === channelType);
    if (!channel) return;

    const recipients = channel.recipients || [];
    
    if (recipients.includes(recipient)) {
      notify('resources.hotels.notifier.validation.recipient_exists', { type: 'warning' });
      return;
    }

    updateChannel(channelType, {
      recipients: [...recipients, recipient],
    });
  };

  /**
   * Remove recipient from channel
   */
  const removeRecipient = (channelType: string, recipient: string) => {
    if (!config) return;

    const channel = config.channels.find(c => c.type === channelType);
    if (!channel) return;

    updateChannel(channelType, {
      recipients: (channel.recipients || []).filter(r => r !== recipient),
    });
  };

  /**
   * Add alert mapping to channel
   */
  const addAlertMapping = (channelType: string) => {
    if (!config) return;

    const channel = config.channels.find(c => c.type === channelType);
    if (!channel) return;
    
    // Find first unused alert type
    const usedTypes = (channel.alertMappings || []).map(m => m.alertType);
    const availableType = ALERT_TYPES.find(t => !usedTypes.includes(t));
    
    if (!availableType) {
      notify('resources.hotels.notifier.validation.all_alerts_mapped', { type: 'warning' });
      return;
    }

    const newMapping: AlertMapping = {
      alertType: availableType,
      useCustomMessage: false,
      templateId: undefined,
      customSubject: undefined,
      customMessage: undefined,
    };

    updateChannel(channelType, {
      alertMappings: [...(channel.alertMappings || []), newMapping],
    });
  };

  /**
   * Remove alert mapping from channel
   */
  const removeAlertMapping = (channelType: string, mappingIndex: number) => {
    if (!config) return;

    const channel = config.channels.find(c => c.type === channelType);
    if (!channel) return;

    updateChannel(channelType, {
      alertMappings: (channel.alertMappings || []).filter((_, i) => i !== mappingIndex),
    });
  };

  /**
   * Update alert mapping
   */
  const updateAlertMapping = (
    channelType: string,
    mappingIndex: number,
    updates: Partial<AlertMapping>
  ) => {
    if (!config) return;

    const channel = config.channels.find(c => c.type === channelType);
    if (!channel) return;

    const updatedMappings = [...(channel.alertMappings || [])];
    updatedMappings[mappingIndex] = {
      ...updatedMappings[mappingIndex],
      ...updates,
    };

    updateChannel(channelType, {
      alertMappings: updatedMappings,
    });
  };

  /**
   * Disable all alerts
   */
  const handleDisableAll = async () => {
    if (!window.confirm(translate('resources.hotels.notifier.confirm.disable_all'))) {
      return;
    }

    setSaving(true);
    try {
      await dataProvider.disableNotifierAlerts(hotelId);
      notify('resources.hotels.notifier.messages.disabled_success', { type: 'success' });
      
      // Reload configuration
      await loadConfiguration();
      setHasChanges(false);
    } catch (error: any) {
      const errorMessage = error?.message || translate('resources.hotels.notifier.messages.disable_error');
      notify(errorMessage, { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <MyFormSection
      title={
        hasChanges 
          ? `${translate('resources.hotels.notifier.title')} *` 
          : translate('resources.hotels.notifier.title')
      }
      subtitle={
        hasChanges
          ? translate('resources.hotels.notifier.subtitle_unsaved')
          : translate('resources.hotels.notifier.subtitle')
      }
      icon={<NotificationsIcon />}
      variant="outlined"
      collapsible={false}
      defaultExpanded={true}
      spacing={3}
      maxContentWidth="100%"
    >
      {loading || !config ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Unsaved Changes Alert */}
          {hasChanges && (
            <Alert severity="warning" sx={{ mb: 0 }}>
              {translate('resources.hotels.notifier.messages.unsaved_changes')}
            </Alert>
          )}

          {/* API Credentials */}
          <Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label={translate('resources.hotels.notifier.fields.apiKey')}
                value={config?.apiKey || ''}
                onChange={(e) => updateCredentials('apiKey', e.target.value)}
                type="password"
                fullWidth
                required
                helperText={translate('resources.hotels.notifier.help.apiKey')}
                disabled={loading || saving}
              />
              
              <TextField
                label={translate('resources.hotels.notifier.fields.accountName')}
                value={config?.accountName || ''}
                onChange={(e) => updateCredentials('accountName', e.target.value)}
                fullWidth
                required
                helperText={translate('resources.hotels.notifier.help.accountName')}
                disabled={loading || saving}
              />
            </Box>
          </Box>

          {/* Fixed Channel Sections */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {translate('resources.hotels.notifier.sections.channels')}
            </Typography>

            {FIXED_CHANNELS.map((channelType) => {
              const channel = config.channels.find(c => c.type === channelType);
              if (!channel) return null;

              return (
                <ChannelCard
                  key={channelType}
                  channel={channel}
                  templates={templates[channelType] || []}
                  loadingTemplates={loadingTemplates}
                  onUpdate={(updates) => updateChannel(channelType, updates)}
                  onAddRecipient={(recipient) => addRecipient(channelType, recipient)}
                  onRemoveRecipient={(recipient) => removeRecipient(channelType, recipient)}
                  onAddMapping={() => addAlertMapping(channelType)}
                  onRemoveMapping={(idx) => removeAlertMapping(channelType, idx)}
                  onUpdateMapping={(idx, updates) => updateAlertMapping(channelType, idx, updates)}
                  translate={translate}
                />
              );
            })}
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              onClick={handleDisableAll}
              disabled={saving}
              color="error"
              variant="outlined"
            >
              {translate('resources.hotels.notifier.actions.disable_all')}
            </Button>
            
            <Button
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={!hasChanges || saving}
              variant="contained"
              color="primary"
            >
              {translate('resources.hotels.notifier.actions.save')}
            </Button>
          </Box>
        </Box>
      )}
    </MyFormSection>
  );
};

/**
 * Channel Card Component
 */
interface ChannelCardProps {
  channel: ChannelConfig;
  templates: NotifierTemplate[];
  loadingTemplates: boolean;
  onUpdate: (updates: Partial<ChannelConfig>) => void;
  onAddRecipient: (recipient: string) => void;
  onRemoveRecipient: (recipient: string) => void;
  onAddMapping: () => void;
  onRemoveMapping: (index: number) => void;
  onUpdateMapping: (index: number, updates: Partial<AlertMapping>) => void;
  translate: (key: string, options?: any) => string;
}

const ChannelCard: React.FC<ChannelCardProps> = ({
  channel,
  templates,
  loadingTemplates,
  onUpdate,
  onAddRecipient,
  onRemoveRecipient,
  onAddMapping,
  onRemoveMapping,
  onUpdateMapping,
  translate,
}) => {
  const [recipientInput, setRecipientInput] = useState('');

  const handleAddRecipient = () => {
    if (recipientInput.trim()) {
      onAddRecipient(recipientInput.trim());
      setRecipientInput('');
    }
  };

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 2,
        mb: 2,
        backgroundColor: 'background.paper',
      }}
    >
      {/* Channel Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getChannelIconComponent(channel.type)}
            <Typography variant="h6">
              {channel.type}
            </Typography>
          </Box>
          
          <FormControlLabel
            control={
              <Switch
                checked={channel.enabled}
                onChange={(e) => onUpdate({ enabled: e.target.checked })}
              />
            }
            label={translate('resources.hotels.notifier.fields.enabled')}
          />
        </Box>
      </Box>

      {/* Recipients */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          {translate('resources.hotels.notifier.fields.recipients')}
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <TextField
            size="small"
            placeholder={getRecipientPlaceholder(channel.type, translate)}
            value={recipientInput}
            onChange={(e) => setRecipientInput(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleAddRecipient();
              }
            }}
            fullWidth
            type={channel.type === 'EMAIL' ? 'email' : 'tel'}
          />
          <Button
            onClick={handleAddRecipient}
            variant="outlined"
            size="small"
          >
            {translate('resources.hotels.notifier.actions.add')}
          </Button>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {(channel.recipients || []).map((recipient, idx) => (
            <Chip
              key={idx}
              label={recipient}
              onDelete={() => onRemoveRecipient(recipient)}
              size="small"
            />
          ))}
        </Box>
      </Box>

      {/* Alert Mappings */}
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2">
            {translate('resources.hotels.notifier.fields.alertMappings')}
          </Typography>
          <Button
            startIcon={<AddIcon />}
            onClick={onAddMapping}
            size="small"
            variant="text"
          >
            {translate('resources.hotels.notifier.actions.add_mapping')}
          </Button>
        </Box>

        {(channel.alertMappings || []).map((mapping, idx) => (
          <AlertMappingRow
            key={idx}
            mapping={mapping}
            templates={templates}
            loadingTemplates={loadingTemplates}
            onUpdate={(updates) => onUpdateMapping(idx, updates)}
            onRemove={() => onRemoveMapping(idx)}
            translate={translate}
          />
        ))}
      </Box>
    </Box>
  );
};

/**
 * Alert Mapping Row Component
 */
interface AlertMappingRowProps {
  mapping: AlertMapping;
  templates: NotifierTemplate[];
  loadingTemplates: boolean;
  onUpdate: (updates: Partial<AlertMapping>) => void;
  onRemove: () => void;
  translate: (key: string, options?: any) => string;
}

const AlertMappingRow: React.FC<AlertMappingRowProps> = ({
  mapping,
  templates,
  loadingTemplates,
  onUpdate,
  onRemove,
  translate,
}) => {
  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 1.5,
        mb: 1,
        backgroundColor: 'action.hover',
      }}
    >
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>{translate('resources.hotels.notifier.fields.alertType')}</InputLabel>
          <Select
            value={mapping.alertType}
            onChange={(e) => onUpdate({ alertType: e.target.value })}
            label={translate('resources.hotels.notifier.fields.alertType')}
          >
            {ALERT_TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                {translate(`resources.hotels.notifier.alertTypes.${type}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControlLabel
          control={
            <Switch
              checked={mapping.useCustomMessage}
              onChange={(e) => onUpdate({ useCustomMessage: e.target.checked })}
            />
          }
          label={translate('resources.hotels.notifier.fields.useCustomMessage')}
        />

        <Box sx={{ flexGrow: 1 }} />

        <IconButton onClick={onRemove} size="small" color="error">
          <DeleteIcon />
        </IconButton>
      </Box>

      {!mapping.useCustomMessage && (
        <Box>
          {loadingTemplates ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">
                {translate('resources.hotels.notifier.messages.loading_templates')}
              </Typography>
            </Box>
          ) : templates.length > 0 ? (
            <Autocomplete
              size="small"
              options={templates}
              getOptionLabel={(option) => `${option.name} - ${option.description || option.id}`}
              value={templates.find(t => t.id === mapping.templateId) || null}
              onChange={(_, newValue) => onUpdate({ templateId: newValue?.id })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={translate('resources.hotels.notifier.fields.template')}
                  placeholder={translate('resources.hotels.notifier.placeholders.selectTemplate')}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {option.name}
                    </Typography>
                    {option.description && (
                      <Typography variant="caption" color="text.secondary">
                        {option.description}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
              fullWidth
            />
          ) : (
            <Alert severity="info" sx={{ mt: 1 }}>
              {translate('resources.hotels.notifier.messages.no_templates')}
            </Alert>
          )}
        </Box>
      )}

      {mapping.useCustomMessage && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <TextField
            size="small"
            label={translate('resources.hotels.notifier.fields.customSubject')}
            value={mapping.customSubject || ''}
            onChange={(e) => onUpdate({ customSubject: e.target.value })}
            fullWidth
          />
          <TextField
            size="small"
            label={translate('resources.hotels.notifier.fields.customMessage')}
            value={mapping.customMessage || ''}
            onChange={(e) => onUpdate({ customMessage: e.target.value })}
            multiline
            rows={3}
            fullWidth
          />
        </Box>
      )}
    </Box>
  );
};